import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { scheduleService } from '../../../services/scheduleService';
import RoomManager from './RoomManager';
import SessionBuilder from './SessionBuilder';
import ScheduleOverview from './ScheduleOverview';

const ProgramTab = ({ conferenceId, conference, tracks, submissions }) => {
  const { t } = useTranslation('conferences');
  const k = (key, opts) => t(`managementDashboard.programTab.${key}`, opts);
  const u = (key) => t(`managementDashboard.programTab.units.${key}`);
  const a = (key, opts) => t(`managementDashboard.programTab.autoSchedule.${key}`, opts);

  const [subTab, setSubTab] = useState('sessions');
  const [rooms, setRooms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Auto-schedule state
  const [showAutoConfig, setShowAutoConfig] = useState(false);
  const [autoScheduling, setAutoScheduling] = useState(false);
  const [autoConfig, setAutoConfig] = useState({
    papersPerSession: '4',
    paperDuration: '20',
    breakDuration: '30',
    lunchDuration: '60',
    dayStartTime: '09:00',
    dayEndTime: '17:00',
  });

  const conferenceDays = scheduleService?.getConferenceDays(conference?.startDate, conference?.endDate);
  const acceptedCount = submissions?.filter(s => s?.status === 'accepted')?.length || 0;

  useEffect(() => {
    loadData();
  }, [conferenceId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [roomsData, sessionsData] = await Promise.all([
        scheduleService?.getRooms(conferenceId),
        scheduleService?.getSessions(conferenceId),
      ]);
      setRooms(roomsData || []);
      setSessions(sessionsData || []);
    } catch (err) {
      console.error('Load schedule data error:', err);
      setError(err?.message || k('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSchedule = async () => {
    if (sessions?.length > 0 && !window.confirm(a('confirmReplace'))) {
      return;
    }
    if (rooms?.length === 0) {
      setError(a('noRooms'));
      return;
    }
    if (acceptedCount === 0) {
      setError(a('noPapers'));
      return;
    }
    try {
      setAutoScheduling(true);
      setError('');
      await scheduleService?.autoSchedule(conferenceId, rooms, tracks, conferenceDays, {
        papersPerSession: parseInt(autoConfig.papersPerSession) || 4,
        paperDuration: parseInt(autoConfig.paperDuration) || 20,
        breakDuration: parseInt(autoConfig.breakDuration) || 30,
        lunchDuration: parseInt(autoConfig.lunchDuration) || 60,
        dayStartTime: autoConfig.dayStartTime || '09:00',
        dayEndTime: autoConfig.dayEndTime || '17:00',
      });
      setShowAutoConfig(false);
      await loadData();
    } catch (err) {
      setError(err?.message || a('failed'));
    } finally {
      setAutoScheduling(false);
    }
  };

  // Small helper so counts read naturally in each language.
  const count = (n, singular, plural) => `${n} ${n === 1 ? u(singular) : u(plural)}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader" size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header with stats & auto-schedule */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">{k('title')}</h2>
          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
            <span>{count(conferenceDays?.length || 0, 'day', 'days')}</span>
            <span>{count(rooms?.length || 0, 'room', 'rooms')}</span>
            <span>{count(sessions?.length || 0, 'session', 'sessions')}</span>
            <span>{count(acceptedCount, 'acceptedPaper', 'acceptedPapers')}</span>
          </div>
        </div>
        <Button
          size="sm"
          iconName="Wand2"
          iconPosition="left"
          onClick={() => setShowAutoConfig(!showAutoConfig)}
        >
          {a('button')}
        </Button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 text-error px-3 py-2 rounded-md text-sm flex items-center gap-2">
          <Icon name="AlertCircle" size={14} />
          {error}
          <button onClick={() => setError('')} className="ms-auto"><Icon name="X" size={14} /></button>
        </div>
      )}

      {/* Auto-schedule config */}
      {showAutoConfig && (
        <div className="bg-muted/50 border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Icon name="Wand2" size={16} className="text-primary" />
            <h3 className="text-sm font-semibold text-foreground">{a('configTitle')}</h3>
          </div>
          <p className="text-xs text-muted-foreground">
            {a('description', {
              papers: acceptedCount,
              days: conferenceDays?.length || 0,
              rooms: rooms?.length || 0
            })}
            {sessions?.length > 0 && (
              <span className="text-warning font-medium">{a('warning', { count: sessions?.length })}</span>
            )}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Input
              label={a('papersPerSession')}
              type="number"
              value={autoConfig.papersPerSession}
              onChange={(e) => setAutoConfig({ ...autoConfig, papersPerSession: e?.target?.value })}
            />
            <Input
              label={a('paperDuration')}
              type="number"
              value={autoConfig.paperDuration}
              onChange={(e) => setAutoConfig({ ...autoConfig, paperDuration: e?.target?.value })}
              description={u('minutes')}
            />
            <Input
              label={a('breakDuration')}
              type="number"
              value={autoConfig.breakDuration}
              onChange={(e) => setAutoConfig({ ...autoConfig, breakDuration: e?.target?.value })}
              description={u('minutes')}
            />
            <Input
              label={a('lunchDuration')}
              type="number"
              value={autoConfig.lunchDuration}
              onChange={(e) => setAutoConfig({ ...autoConfig, lunchDuration: e?.target?.value })}
              description={u('minutes')}
            />
            <Input
              label={a('dayStarts')}
              type="time"
              value={autoConfig.dayStartTime}
              onChange={(e) => setAutoConfig({ ...autoConfig, dayStartTime: e?.target?.value })}
            />
            <Input
              label={a('dayEnds')}
              type="time"
              value={autoConfig.dayEndTime}
              onChange={(e) => setAutoConfig({ ...autoConfig, dayEndTime: e?.target?.value })}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleAutoSchedule} loading={autoScheduling} iconName="Wand2" iconPosition="left">
              {a('generate')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAutoConfig(false)}>{a('cancel')}</Button>
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="border-b border-border">
        <nav className="flex gap-0">
          {[
            { key: 'rooms', icon: 'DoorOpen', count: rooms?.length },
            { key: 'sessions', icon: 'Calendar', count: sessions?.length },
            { key: 'overview', icon: 'LayoutGrid' },
          ]?.map(tab => (
            <button
              key={tab?.key}
              onClick={() => setSubTab(tab?.key)}
              className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-250 whitespace-nowrap flex items-center gap-1.5 ${
                subTab === tab?.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              {k(`subTabs.${tab?.key}`)}
              {tab?.count !== undefined && (
                <span className="ms-1 text-[10px] px-1.5 py-0.5 bg-muted">{tab?.count}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {subTab === 'rooms' && (
        <RoomManager conferenceId={conferenceId} rooms={rooms} onRefresh={loadData} />
      )}
      {subTab === 'sessions' && (
        <SessionBuilder
          conferenceId={conferenceId}
          conference={conference}
          sessions={sessions}
          rooms={rooms}
          tracks={tracks}
          conferenceDays={conferenceDays}
          onRefresh={loadData}
        />
      )}
      {subTab === 'overview' && (
        <ScheduleOverview
          conferenceId={conferenceId}
          sessions={sessions}
          rooms={rooms}
          conferenceDays={conferenceDays}
          onRefresh={loadData}
        />
      )}
    </div>
  );
};

export default ProgramTab;
